import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@/design-system/primitives'
import {
  useDoseChangeReview,
  useKeepDoseChangeHold,
  type DoseChangeHold,
} from '@/lib/api/doseChangeHolds'
import { useRequestClinicianVisit } from '@/lib/api/clinicianHooks'
import { ApiError } from '@/lib/api/client'
import { DoseRequestMessage } from './DoseRequestMessage'

export const PRESCRIBER_ONLY = "Only the patient's prescriber can review this request."

/**
 * Resolve one patient dose-change request without hunting for an order.
 *
 * An awaiting order for the treatment is the work item (deep link, or a visit
 * request on it). Keeping the current dose needs no order: the server binds the
 * single active chart prescription and fails closed otherwise. A change with no
 * awaiting order creates nothing here: future fills stay held and the next one
 * reaches the Ready list as an order.
 */
export function DoseChangeReviewSheet({ hold }: { hold: DoseChangeHold }) {
  const review = useDoseChangeReview(hold.patient_id, hold.id)
  const keep = useKeepDoseChangeHold(hold.patient_id)
  const visit = useRequestClinicianVisit(hold.patient_id)
  const [note, setNote] = useState('')
  const [confirmed, setConfirmed] = useState(false)
  const [visitNote, setVisitNote] = useState('')
  const [refreshing, setRefreshing] = useState(false)

  // Every chart refresh goes through here: a keep refused because the
  // prescription changed clears only once fresh chart data has arrived, so the
  // refused prescription is never offered again.
  const refreshReview = async () => {
    setRefreshing(true)
    const fresh = await review.refetch()
    setRefreshing(false)
    if (fresh.isSuccess && keep.isError) {
      setConfirmed(false)
      keep.reset()
    }
  }

  if (review.isPending)
    return (
      <p role="status" className="text-14 text-ink-muted">
        Checking the chart prescription…
      </p>
    )
  // Only real prescribers can review; anyone else is refused, which isn't a
  // chart problem and won't change on retry.
  if (
    review.error instanceof ApiError &&
    review.error.status === 403 &&
    review.error.code === 'prescriber_required'
  )
    return <p className="text-14 text-ink-muted">{PRESCRIBER_ONLY}</p>
  if (review.isError || !review.data)
    return (
      <p role="alert" className="text-14 text-danger">
        The chart prescription could not be checked.{' '}
        <button
          className="underline"
          disabled={refreshing}
          onClick={() => void refreshReview()}
        >
          Retry
        </button>
      </p>
    )
  const data = review.data
  const rx = data.active_prescription
  const order = data.awaiting_order
  const kept = keep.isSuccess
  // The server refuses to bind a prescription other than the one shown here.
  const prescriptionChanged =
    (keep.error as { code?: string } | null)?.code ===
    'dose_change_prescription_changed'

  return (
    <div className="flex flex-col gap-12 pt-8">
      <DoseRequestMessage hold={hold} />
      {hold.source_channel === 'healthie_chat' &&
        /^\d+$/.test(hold.conversation_id) && (
          <Link
            className="underline text-14"
            to={`/clinician-portal/inbox/${hold.conversation_id}`}
          >
            Open source conversation
          </Link>
        )}
      <div className="text-14">
        <p className="text-12 text-ink-muted">Current prescription</p>
        {rx ? (
          <>
            <p className="font-medium">{rx.name}</p>
            <p className="whitespace-pre-wrap">{rx.directions}</p>
          </>
        ) : (
          <p>No single active prescription for this treatment on the chart.</p>
        )}
      </div>

      {order ? (
        <div className="flex flex-col gap-8">
          <p className="text-14">
            {order.order_number || 'An order'} is waiting for your review.
            Change the dose or directions there.
          </p>
          <Link
            className="underline text-14"
            to={`/clinician-portal/today/${encodeURIComponent(order.id)}`}
          >
            Open order in Prescribing
          </Link>
          <label className="flex flex-col gap-4 text-14">
            Note for a visit (optional)
            <textarea
              rows={2}
              value={visitNote}
              onChange={(event) => setVisitNote(event.target.value)}
              className="border border-line p-8"
            />
          </label>
          <Button
            type="button"
            variant="secondary"
            disabled={visit.isPending || visit.isSuccess}
            onClick={() =>
              visit.mutate({
                order_id: order.id,
                reason_code: 'discuss_dosing',
                note: visitNote.trim() || undefined,
              })
            }
          >
            {visit.isSuccess ? 'Visit requested' : 'Discuss at a visit first'}
          </Button>
          {visit.isError && (
            <p role="alert" className="text-14 text-danger">
              {visit.error instanceof Error
                ? visit.error.message
                : 'The visit request could not be recorded.'}
            </p>
          )}
        </div>
      ) : (
        <p className="text-14">
          No order to change yet. Future fills stay on hold; the next renewal or
          fill comes to your Ready list as an order where you can change the
          dose or directions.
        </p>
      )}

      {kept ? (
        <p role="status" className="text-14">
          Kept the current dose and directions. Fills can resume.
        </p>
      ) : prescriptionChanged ? (
        <p role="alert" className="text-14 text-danger">
          The chart prescription changed after you opened this review, so
          nothing was recorded.{' '}
          <button
            className="underline"
            disabled={refreshing}
            onClick={() => void refreshReview()}
          >
            {refreshing
              ? 'Checking the chart prescription…'
              : 'Review the current prescription'}
          </button>
        </p>
      ) : data.keep.available && rx ? (
        <form
          className="flex flex-col gap-8"
          onSubmit={(event) => {
            event.preventDefault()
            keep.mutate({
              requestId: hold.id,
              body: {
                note: note.trim(),
                confirmed: true,
                expected_resolution_version: hold.resolution_version,
                expected_medication_id: rx.medication_id,
                expected_instructions_sha256: rx.instructions_sha256,
              },
            })
          }}
        >
          <label className="flex flex-col gap-4 text-14">
            Reason for keeping the current dose
            <textarea
              rows={2}
              required
              value={note}
              onChange={(event) => setNote(event.target.value)}
              className="border border-line p-8"
            />
          </label>
          <label className="flex gap-8 items-center text-14">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(event) => setConfirmed(event.target.checked)}
            />
            I reviewed this request against the current prescription.
          </label>
          <Button
            type="submit"
            disabled={note.trim().length < 5 || !confirmed || keep.isPending}
          >
            Keep current dose
          </Button>
          {keep.isError && (
            <p role="alert" className="text-14 text-danger">
              {keep.error instanceof Error
                ? keep.error.message
                : 'The decision could not be recorded.'}
            </p>
          )}
        </form>
      ) : (
        <p className="text-14">
          Keeping the current dose can't be confirmed here: {data.keep.reason}
        </p>
      )}
      <Link
        className="underline text-12"
        to={`/clinician-portal/patients/${encodeURIComponent(hold.patient_id)}`}
      >
        Open patient chart
      </Link>
    </div>
  )
}
